// src/utils/acceptance.ts
// ─────────────────────────────────────────────────────────────────────────
// Legal acceptance: SERVER-AUTHORITATIVE.
//
//   Server (POST/GET /v1/acceptance) = the legal record and the source of
//     truth for whether a user may enter the app.
//   localStorage (this file)         = a UX CACHE ONLY. It exists to avoid
//     flashing the acceptance gate on every boot while the server is checked.
//     It NEVER grants access on its own and MUST NOT override the server.
//
// Hard rule: local "accepted" only suppresses the immediate modal flash while
// the background server check runs. If the server says missing/stale, or the
// check can't be completed when a fresh check is required, the gate blocks.
// ─────────────────────────────────────────────────────────────────────────

import {
	TOS_VERSION,
	PRIVACY_VERSION,
	DISCLAIMER_VERSION,
	AGE_VERSION,
	ACCEPTANCE_DOCS,
} from '../constants/DISCLAIMERS';
import { syncClient } from '../sync/webSync';
import type {
	AcceptanceStatusResponseWire,
	AcceptanceDocTypeWire,
} from '@nisk/core';

// ── Local cache shape (UX only, never authoritative) ────────────────────────
// Minimal memory of what the SERVER has already confirmed, so we don't flash the
// gate while re-verifying. No legal confidence is derived from this.
export interface AcceptanceCache {
	uid: string;
	tosVersion: string;
	privacyVersion: string;
	disclaimerVersion: string;
	ageVersion: string;
	acceptedAt: string; // ISO — when the user accepted (server-recorded moment)
	cachedAt: string; // ISO — when this cache row was written locally
}

const KEY = (uid: string) => `acceptance:${uid}`;
const WELCOME_KEY = (uid: string) => `welcome_seen:${uid}`;

/** True if a cache row matches ALL current doc versions (fast, non-authoritative). */
function cacheIsCurrent(c: AcceptanceCache): boolean {
	return (
		c.tosVersion === TOS_VERSION &&
		c.privacyVersion === PRIVACY_VERSION &&
		c.disclaimerVersion === DISCLAIMER_VERSION &&
		c.ageVersion === AGE_VERSION
	);
}

/** Write the local cache after the SERVER has recorded acceptance. */
export function writeAcceptanceCache(uid: string): AcceptanceCache {
	const now = new Date().toISOString();
	const cache: AcceptanceCache = {
		uid,
		tosVersion: TOS_VERSION,
		privacyVersion: PRIVACY_VERSION,
		disclaimerVersion: DISCLAIMER_VERSION,
		ageVersion: AGE_VERSION,
		acceptedAt: now,
		cachedAt: now,
	};
	try {
		localStorage.setItem(KEY(uid), JSON.stringify(cache));
	} catch {
		// Storage unavailable (private mode / quota). Non-fatal: the server is
		// authoritative, so the only cost is not being able to skip the flash.
	}
	return cache;
}

/** Read the local cache row for a UID, or null. */
export function getAcceptanceCache(uid: string): AcceptanceCache | null {
	try {
		const raw = localStorage.getItem(KEY(uid));
		if (!raw) return null;
		return JSON.parse(raw) as AcceptanceCache;
	} catch {
		return null;
	}
}

/**
 * Whether the local cache (UX only) currently looks satisfied for this UID.
 * Used ONLY to decide whether to suppress the immediate gate flash while the
 * background server check runs — never to grant access on its own.
 */
export function cacheLooksAccepted(uid: string): boolean {
	const c = getAcceptanceCache(uid);
	return c !== null && c.uid === uid && cacheIsCurrent(c);
}

// ── Server-authoritative operations ─────────────────────────────────────────

/**
 * The doc types + versions the user is currently required to have accepted.
 * The server records these as four separate rows; the UI presents them as two
 * checkboxes.
 */
export const REQUIRED_ACCEPTANCE = ACCEPTANCE_DOCS;

/**
 * Record the current acceptance to the SERVER (all four doc types), then, only
 * on full success, write the local cache. THROWS if any POST fails — callers
 * MUST treat a throw as "not accepted; do not enter the app" and surface the
 * blocking failure screen. Never writes the local cache on partial failure.
 */
export async function recordAcceptanceToServer(uid: string): Promise<void> {
	// Sequential rather than Promise.all: on failure we stop at the first error
	// and surface it, and the append-only server tolerates a retry re-sending
	// the ones that already landed (each is an independent legal event).
	for (const doc of REQUIRED_ACCEPTANCE) {
		await syncClient.recordAcceptance({
			docType: doc.docType as AcceptanceDocTypeWire,
			version: doc.version,
		});
	}
	writeAcceptanceCache(uid);
}

export type ServerAcceptanceState = 'current' | 'stale-or-missing';

/**
 * Ask the SERVER whether the user has current acceptance for every required
 * doc type/version. This is the authority for app access. THROWS on a network/
 * server failure so the caller can show a soft error state rather than trusting
 * the local cache when a fresh check was required.
 */
export async function checkServerAcceptance(
	uid: string,
): Promise<ServerAcceptanceState> {
	const status: AcceptanceStatusResponseWire =
		await syncClient.getAcceptanceStatus();

	const acceptedVersionByDoc = new Map<string, string>(
		status.accepted.map((a) => [a.docType, a.version]),
	);

	const allCurrent = REQUIRED_ACCEPTANCE.every(
		(doc) => acceptedVersionByDoc.get(doc.docType) === doc.version,
	);

	if (allCurrent) {
		// Server confirms — refresh the cache so future boots skip the flash.
		writeAcceptanceCache(uid);
		return 'current';
	}
	return 'stale-or-missing';
}

// ── Welcome modal (one-time per user) ──────────────────────────────────────

/** True if this user has already dismissed the first-time welcome modal. */
export function hasSeenWelcome(uid: string): boolean {
	try {
		return localStorage.getItem(WELCOME_KEY(uid)) === 'true';
	} catch {
		return true; // fail closed — don't nag if storage is broken
	}
}

/** Mark the welcome modal as seen for this user. */
export function markWelcomeSeen(uid: string): void {
	try {
		localStorage.setItem(WELCOME_KEY(uid), 'true');
	} catch {
		/* noop */
	}
}

// ── Profile-touched flag (Impact Lab → Simulate handoff gating) ─────────────
// The financial profile ships with seeded defaults, so "has a profile" is
// always true. What we actually want to know is whether the user has ever
// personalized their numbers. Set on the first real financial edit (see
// localStorageMiddleware); read by Impact Lab to decide whether "Run this on
// my life" can go straight to Simulate or should detour through the Dashboard.
// Not keyed by UID — Impact Lab is usable pre-auth.

const PROFILE_TOUCHED_KEY = 'nis.profileTouched';

/** True once the user has made a real edit to their financial profile. */
export function hasTouchedProfile(): boolean {
	try {
		return localStorage.getItem(PROFILE_TOUCHED_KEY) === '1';
	} catch {
		return false; // fail open → detour to Dashboard, the safe momentum-preserving path
	}
}

/** Mark the profile as personalized. Idempotent. */
export function markProfileTouched(): void {
	try {
		localStorage.setItem(PROFILE_TOUCHED_KEY, '1');
	} catch {
		/* noop */
	}
}

// ── Pending Impact Lab handoff (preserved across the Dashboard detour) ──────
// When the CTA fires but the profile is untouched, we stash the intended
// scenario so that after Dashboard setup we can resume with "Ready — now run
// 'New car vs used car' on your life."

const PENDING_HANDOFF_KEY = 'nis.impact.pendingHandoff';

export interface PendingImpactHandoff {
	scenarioId: string;
	inputs: Record<string, number>;
	label: string; // scenario label, for the resume prompt copy
	savedAt: number;
}

export function setPendingImpactHandoff(h: PendingImpactHandoff): void {
	try {
		localStorage.setItem(PENDING_HANDOFF_KEY, JSON.stringify(h));
	} catch {
		/* noop */
	}
}

export function getPendingImpactHandoff(): PendingImpactHandoff | null {
	try {
		const raw = localStorage.getItem(PENDING_HANDOFF_KEY);
		return raw ? (JSON.parse(raw) as PendingImpactHandoff) : null;
	} catch {
		return null;
	}
}

export function clearPendingImpactHandoff(): void {
	try {
		localStorage.removeItem(PENDING_HANDOFF_KEY);
	} catch {
		/* noop */
	}
}
